import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building2, Globe, Plug, Sparkles } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useOptionalAuth } from '@/components/AuthProvider';
import { cn } from '@/lib/utils';
import {
  WORKSPACE_SETTINGS_SECTIONS,
  resolveWorkspaceSettingsSection,
  visibleWorkspaceSettingsSections,
  type WorkspaceSettingsSection,
} from '@/lib/workspaces/workspaceSettingsNav';

/**
 * Loading states for Workspace settings (`/home/workspace-settings`).
 *
 * Every section used to open on its own spinner, after the route's bare
 * `<Suspense>` spinner and a blank `RoleRoute` frame — and Brand websites and
 * AI Knowledge also flashed "Select a workspace…" while the workspace list was
 * still loading. The route now draws the page's own nav with the requested
 * section's skeleton, and each section keeps that same shape until its data
 * lands. Imported eagerly into `App.tsx`, so keep it light.
 */

const SECTION_ICON: Record<WorkspaceSettingsSection, React.ComponentType<{ className?: string }>> = {
  profile: Building2,
  mcp: Plug,
  'brand-websites': Globe,
  'ai-knowledge': Sparkles,
};

/** Mirrors `McpSection` / the settings cards: title + description header, padded body. */
const SettingsCardSkeleton: React.FC<{ action?: boolean; children: React.ReactNode }> = ({ action, children }) => (
  <section className="overflow-hidden rounded-2xl border border-[#EAECF0] bg-white">
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[#EAECF0] px-5 py-4 sm:px-6 sm:py-5">
      <div className="min-w-0 flex-1 space-y-1.5">
        <Skeleton className="h-4 w-36 rounded" />
        <Skeleton className="h-3.5 w-3/4 max-w-md rounded" />
      </div>
      {action ? <Skeleton className="h-9 w-[104px] shrink-0 rounded-lg" /> : null}
    </div>
    <div className="space-y-3 px-5 py-5 sm:px-6 sm:py-6">{children}</div>
  </section>
);

/** A mono read-only field (token, IDs, URLs) still loading. */
export const McpFieldSkeleton: React.FC = () => (
  <Skeleton className="h-10 w-full rounded-lg" data-testid="mcp-field-skeleton" />
);

/** The Desktop MCP Config block still loading. */
export const McpConfigSkeleton: React.FC = () => (
  <div className="space-y-2 rounded-lg border border-[#D0D5DD] bg-[#F9FAFB] p-3.5" data-testid="mcp-config-skeleton">
    {[40, 55, 30, 65, 70, 50, 60, 35].map((w, i) => (
      <Skeleton key={i} className="h-3 rounded bg-[#EAECF0]" style={{ width: `${w}%` }} />
    ))}
  </div>
);

/** The token card's action buttons still loading. */
export const McpTokenActionsSkeleton: React.FC = () => (
  <Skeleton className="h-9 w-[104px] rounded-lg" data-testid="mcp-token-actions-skeleton" />
);

/** Whole MCP section — used by the route before the page's code has loaded. */
export const McpSectionSkeleton: React.FC = () => (
  <div className="space-y-4">
    <div className="flex items-start justify-between gap-3">
      <div className="space-y-1.5">
        <Skeleton className="h-5 w-40 rounded" />
        <Skeleton className="h-3.5 w-72 max-w-full rounded" />
      </div>
      <Skeleton className="h-9 w-[112px] rounded-lg" />
    </div>
    <SettingsCardSkeleton action><McpFieldSkeleton /></SettingsCardSkeleton>
    <SettingsCardSkeleton><McpFieldSkeleton /></SettingsCardSkeleton>
    <SettingsCardSkeleton><McpConfigSkeleton /></SettingsCardSkeleton>
  </div>
);

/** Mirrors `WorkspaceProfileSettings`: heading, name, image, default toggle, delete card. */
export const WorkspaceProfileSkeleton: React.FC = () => (
  <div className="space-y-8" data-testid="workspace-settings-profile-skeleton">
    <Skeleton className="h-7 w-32 rounded" />
    <div className="space-y-1.5">
      <Skeleton className="h-3.5 w-28 rounded" />
      <Skeleton className="h-10 w-full rounded-lg" />
    </div>
    <div className="space-y-1.5">
      <Skeleton className="h-3.5 w-32 rounded" />
      <div className="flex items-center gap-3">
        <Skeleton className="h-16 w-16 shrink-0 rounded-full" />
        <div className="space-y-2">
          <Skeleton className="h-9 w-[92px] rounded-lg" />
          <Skeleton className="h-3 w-48 rounded" />
        </div>
      </div>
    </div>
    <div className="space-y-2">
      <Skeleton className="h-3.5 w-32 rounded" />
      <div className="flex items-center gap-3">
        <Skeleton className="h-5 w-9 rounded-full" />
        <Skeleton className="h-3.5 w-56 rounded" />
      </div>
    </div>
    <div className="overflow-hidden rounded-2xl border border-[#EAECF0] bg-white px-5 py-5 sm:px-6 sm:py-6">
      <div className="flex items-center justify-between gap-3">
        <div className="space-y-1.5">
          <Skeleton className="h-4 w-36 rounded" />
          <Skeleton className="h-3.5 w-64 max-w-full rounded" />
        </div>
        <Skeleton className="h-9 w-[80px] rounded-lg" />
      </div>
    </div>
  </div>
);

/**
 * Mirrors `BrandWebsitesSettings`. The heading and description are static
 * copy, so they render for real; only the website cards and the add row are
 * placeholders.
 */
export const BrandWebsitesSkeleton: React.FC<{ rows?: number }> = ({ rows = 2 }) => (
  <section
    className="overflow-hidden rounded-2xl border border-[#EAECF0] bg-white"
    aria-busy="true"
    data-testid="brand-websites-skeleton"
  >
    <div className="border-b border-[#EAECF0] px-5 py-4 sm:px-6 sm:py-5">
      <h2 className="text-[15px] font-semibold text-[#101828]">Brand Websites</h2>
      <p className="mt-0.5 text-[13px] text-[#667085]">
        Add your websites to auto-import brand colors, logo &amp; style for AI image generation. Click a website to view details, or the pencil icon to edit.
      </p>
    </div>
    <div className="space-y-3 px-5 py-5 sm:px-6 sm:py-6">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 rounded-xl border border-[#EAECF0] bg-white p-3">
          <Skeleton className="h-10 w-10 shrink-0 rounded-lg" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton className="h-4 w-40 rounded" />
            <Skeleton className="h-3.5 w-56 max-w-full rounded" />
          </div>
          <Skeleton className="h-8 w-8 shrink-0 rounded-md" />
          <Skeleton className="h-8 w-8 shrink-0 rounded-md" />
        </div>
      ))}
      <div className="flex gap-2">
        <Skeleton className="h-10 min-w-0 flex-1 rounded-lg" />
        <Skeleton className="h-10 w-[72px] shrink-0 rounded-lg" />
      </div>
    </div>
  </section>
);

/**
 * Mirrors the `OrganizationAiContextForm` fields: inputs, the services
 * textarea, the two-up contacts, notes — and, for editors, the Clear / Save
 * row (read-only viewers never get one).
 */
export const AiKnowledgeFieldsSkeleton: React.FC<{ withActions?: boolean }> = ({ withActions = true }) => {
  const field = (labelW: string, h: string) => (
    <div className="space-y-1.5">
      <Skeleton className={cn('h-3.5 rounded', labelW)} />
      <Skeleton className={cn('w-full rounded-lg', h)} />
    </div>
  );
  return (
    <div className="space-y-4" aria-busy="true" data-testid="ai-knowledge-skeleton">
      {field('w-28', 'h-10')}
      {field('w-20', 'h-10')}
      {field('w-32', 'h-10')}
      {field('w-44', 'h-[118px]')}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {field('w-28', 'h-10')}
        {field('w-28', 'h-10')}
      </div>
      {field('w-52', 'h-[98px]')}
      {withActions ? (
        <div className="flex items-center justify-between gap-2 pt-2">
          <Skeleton className="h-9 w-[92px] rounded-lg" />
          <Skeleton className="h-9 w-[72px] rounded-lg" />
        </div>
      ) : null}
    </div>
  );
};

/** The AI Knowledge section card — static header text, placeholder fields. */
const AiKnowledgeSectionSkeleton: React.FC = () => (
  <section className="overflow-hidden rounded-2xl border border-[#EAECF0] bg-white">
    <div className="border-b border-[#EAECF0] px-5 py-4 sm:px-6 sm:py-5">
      <h2 className="text-[15px] font-semibold text-[#101828]">AI Knowledge Base</h2>
      <p className="mt-0.5 text-[13px] text-[#667085]">
        Tell the AI about your organization so it can answer customer questions accurately
        on Instagram, WhatsApp, and beyond. Fill this in once — every AI flow you create
        will use it.
      </p>
    </div>
    <div className="px-5 py-5 sm:px-6 sm:py-6">
      <AiKnowledgeFieldsSkeleton />
    </div>
  </section>
);

const SECTION_SKELETON: Record<WorkspaceSettingsSection, React.FC> = {
  profile: WorkspaceProfileSkeleton,
  mcp: McpSectionSkeleton,
  'brand-websites': () => <BrandWebsitesSkeleton />,
  'ai-knowledge': AiKnowledgeSectionSkeleton,
};

/**
 * The page-level placeholder for the route guard and the `<Suspense>` fallback:
 * the real nav (same items and active section the page will show) beside the
 * requested section's skeleton.
 */
export const WorkspaceSettingsPageSkeleton: React.FC = () => {
  const [searchParams] = useSearchParams();
  const user = useOptionalAuth()?.user ?? null;
  const section = resolveWorkspaceSettingsSection(searchParams.get('section'), user);
  const visible = new Set(visibleWorkspaceSettingsSections(user));
  const Section = SECTION_SKELETON[section];

  return (
    <div
      className="flex h-full min-h-0 w-full bg-white"
      aria-busy="true"
      aria-label="Loading workspace settings"
      data-testid="workspace-settings-page-skeleton"
    >
      <aside className="hidden h-full w-[240px] shrink-0 overflow-y-auto border-r border-[#EAECF0] bg-white px-3 py-6 md:block">
        <div className="px-2 pb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#98A2B3]">
          Workspace settings
        </div>
        <div className="space-y-0.5">
          {WORKSPACE_SETTINGS_SECTIONS.filter((s) => visible.has(s.id)).map((s) => {
            const Icon = SECTION_ICON[s.id];
            const active = s.id === section;
            return (
              <div
                key={s.id}
                className={cn(
                  'flex h-10 w-full items-center gap-2.5 rounded-lg px-2.5 text-[14px] font-medium',
                  active ? 'bg-brand-soft text-[#101828]' : 'text-[#344054]',
                )}
              >
                <Icon className={cn('h-4 w-4 shrink-0', active ? 'text-brand' : 'text-[#667085]')} />
                <span className="truncate">{s.label}</span>
              </div>
            );
          })}
        </div>
      </aside>

      <div className="min-h-0 min-w-0 flex-1 overflow-hidden bg-white">
        <div className="mb-4 px-[15px] pt-3 md:hidden">
          <Skeleton className="h-10 w-full rounded-lg" />
        </div>
        <div className="mx-auto max-w-3xl space-y-4 px-[15px] py-6">
          <Section />
        </div>
      </div>
    </div>
  );
};

export default WorkspaceSettingsPageSkeleton;
